import React, { useState, useRef, useCallback, useEffect } from 'react';

const CLOSE_DELAY_MS = 200;

/**
 * Shared interactive popover: stays open when moving pointer from trigger
 * to popover body, with optional click-to-pin for touch/mobile.
 */
function InteractivePopover({
  trigger,
  children,
  className = '',
  popoverClassName = '',
  placement = 'bottom',
  maxHeight = 240,
}) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    if (pinned) return;
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimer, pinned]);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popoverWidth = 380;
    let top = rect.bottom + 8;
    let left = Math.min(rect.left, window.innerWidth - popoverWidth - 16);

    if (placement === 'top') {
      top = rect.top - 8;
    }

    setPopoverPos({ top, left });
  }, [placement]);

  const handleOpen = useCallback(() => {
    clearCloseTimer();
    updatePosition();
    setOpen(true);
  }, [clearCloseTimer, updatePosition]);

  const handleTriggerClick = useCallback((e) => {
    e.stopPropagation();
    if (open && pinned) {
      setPinned(false);
      setOpen(false);
    } else {
      setPinned(true);
      handleOpen();
    }
  }, [open, pinned, handleOpen]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePosition]);

  return (
    <span
      ref={triggerRef}
      className={`interactive-popover-trigger ${className}`}
      onMouseEnter={handleOpen}
      onMouseLeave={scheduleClose}
      onFocus={handleOpen}
      onBlur={scheduleClose}
      onClick={handleTriggerClick}
    >
      {trigger}
      {open && (
        <span
          ref={popoverRef}
          className={`interactive-popover ${popoverClassName}`}
          role="tooltip"
          style={{
            top: popoverPos.top,
            left: popoverPos.left,
            maxHeight: `${maxHeight}px`,
          }}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleClose}
        >
          <div
            className="interactive-popover-content"
            style={{ maxHeight: `${maxHeight}px` }}
          >
            {children}
          </div>
        </span>
      )}
    </span>
  );
}

export default InteractivePopover;
